import { useState } from "react";
import { ArrowLeft16Regular, ArrowRight16Regular } from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHead,
  Panel,
  Pill,
  Toggle,
  SelectX,
  Skeleton,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { fieldErrorsOf, useGet, useHandleApiError, useWrite, type Schema } from "../api";
import { ConfirmDialog } from "../components";
import { formatDate, formatMoney, formatNumber } from "../format";

type Tier = Schema<"PlanTier">;
type Override = Schema<"PriceOverride">;
type Customer = Schema<"Customer">;

const OVERRIDE_TYPE = { custom_price: "Custom price", teams_limit: "Teams limit" } as const;
const DAY = 86_400_000;
const euros = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? "" : String(cents / 100);
const toCents = (v: string): number => Math.round(Number(v) * 100);

/**
 * Tier definitions (price, Teams limit) and per-customer exceptions are two separate write paths (BR-247, BR-248). The tier
 * limit is the only entitlement a plan carries: there is no feature checklist and no plan-gated feature scope.
 */
export function Plans() {
  const tiers = useGet<Tier[]>("/plans");
  const overrides = useGet<Override[]>("/price-overrides");
  const fail = useHandleApiError();
  const toast = useToast();
  const del = useWrite("DELETE", "/price-overrides/:id", ["/price-overrides", "/customers"]);
  const reorder = useWrite<Tier[], Schema<"PlanOrder">>("PUT", "/plan-order", ["/plans"]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Override | null>(null);
  const columns: Column<Override>[] = [
    {
      key: "c",
      header: "Customer",
      render: (o) => (
        <Link to={`/customers/${o.organizationId}`} className="t-body-strong">
          {o.customerName}
        </Link>
      ),
    },
    {
      key: "t",
      header: "Override",
      render: (o) => <Pill variant="brand">{OVERRIDE_TYPE[o.type ?? "custom_price"]}</Pill>,
    },
    {
      key: "v",
      header: "Value",
      render: (o) =>
        o.type === "custom_price"
          ? formatMoney(o.valueCents)
          : o.valueInt === -1
            ? "Unlimited"
            : formatNumber(o.valueInt ?? 0),
    },
    { key: "r", header: "Reason", render: (o) => <span title={o.reason}>{o.reason}</span> },
    { key: "a", header: "Approved by", render: (o) => `${o.approvedBy} · ${formatDate(o.createdAt)}` },
    {
      key: "x",
      header: "Actions",
      render: (o) => (
        <Button variant="ghost" onClick={() => setRemoving(o)}>
          Remove
        </Button>
      ),
    },
  ];
  return (
    <div className="gp-page">
      <PageHead
        title="Plans & pricing"
        subtitle="Four standard tiers in the order customers see them, plus exceptions negotiated per customer."
      />
      <h2 className="t-title-sm" style={{ margin: 0 }}>
        Plan tiers
      </h2>
      {tiers.isError ? (
        <Banner tone="danger" action={<Button onClick={() => void tiers.refetch()}>Retry</Button>}>
          Could not load the plans.
        </Banner>
      ) : null}
      {!tiers.data ? (
        <Skeleton rows={4} />
      ) : (
        <div className="gp-grid-4">
          {tiers.data.map((t, i) => (
            <TierCard
              key={t.planKey}
              tier={t}
              position={{ index: i, count: tiers.data?.length ?? 0 }}
              moving={reorder.isPending}
              onMove={(delta) => {
                const keys = (tiers.data ?? []).map((x) => x.planKey ?? "");
                const [moved] = keys.splice(i, 1);
                keys.splice(i + delta, 0, moved ?? "");
                reorder.mutate(
                  { body: { planKeys: keys as Schema<"PlanOrder">["planKeys"] } },
                  { onError: fail },
                );
              }}
            />
          ))}
        </div>
      )}
      <section className="gp-stack" aria-labelledby="ov-h">
        <div className="gp-section-head">
          <h2 id="ov-h" className="t-title-sm" style={{ margin: 0 }}>
            Customer overrides
          </h2>
          <Button variant="primary" onClick={() => setAdding(true)}>
            New override
          </Button>
        </div>
        <Panel label="Customer overrides">
          <DataTable
            caption="Customer overrides"
            columns={columns}
            rows={overrides.data}
            loading={overrides.isLoading}
            error={overrides.isError}
            onRetry={() => void overrides.refetch()}
            errorTitle="Could not load the overrides."
            retryLabel="Retry"
            rowKey={(o) => o.id ?? ""}
            emptyTitle="No overrides"
          />
        </Panel>
      </section>
      <ConfirmDialog
        open={removing !== null}
        danger
        title={`Remove the override of ${removing?.customerName}?`}
        description="The customer goes back to the standard tier price and limit from their next billing period."
        confirmLabel="Remove override"
        busy={del.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          del.mutate(
            { params: { id: removing.id ?? "" } },
            {
              onSuccess: () => {
                toast.success("Override removed");
                setRemoving(null);
              },
              onError: fail,
            },
          )
        }
      />
      <NewOverride open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function TierCard({
  tier,
  position,
  moving,
  onMove,
}: {
  tier: Tier;
  position: { index: number; count: number };
  moving: boolean;
  onMove: (delta: -1 | 1) => void;
}) {
  const fail = useHandleApiError();
  const toast = useToast();
  const save = useWrite<Tier, Schema<"PlanTierUpdate">>("PUT", "/plans/:key", ["/plans"]);
  const [name, setName] = useState(tier.displayName ?? "");
  const [limit, setLimit] = useState(String(tier.teamsLimit ?? 0));
  const [monthly, setMonthly] = useState(euros(tier.monthlyPriceCents));
  const [annual, setAnnual] = useState(euros(tier.annualPriceCents));
  const [scheduling, setScheduling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [popular, setPopular] = useState(tier.popular === true);
  const [errs, setErrs] = useState<Record<string, string>>({});
  // the price is typed in the tier while nobody pays it; afterwards it changes through an announced price change (BR-247)
  const editablePrice = tier.priceEditable === true && tier.planKey !== "enterprise";
  const priceDirty =
    editablePrice && (monthly !== euros(tier.monthlyPriceCents) || annual !== euros(tier.annualPriceCents));
  const dirty =
    name !== (tier.displayName ?? "") ||
    Number(limit) !== tier.teamsLimit ||
    popular !== (tier.popular === true) ||
    priceDirty;
  const badPrice =
    priceDirty && (monthly === "" || !(Number(monthly) >= 0) || (annual !== "" && !(Number(annual) >= 0)));
  const invalid = !name.trim() || !Number.isInteger(Number(limit)) || Number(limit) < -1 || badPrice;
  const pending = tier.pendingPriceChange;
  return (
    <Card
      title={
        <>
          {tier.displayName ?? tier.planKey ?? ""}
          {tier.popular ? (
            <>
              {" "}
              <Pill variant="brand">Most popular</Pill>
            </>
          ) : null}
        </>
      }
      actions={
        <span className="gp-actions">
          <Button
            variant="ghost"
            aria-label={`Move ${tier.displayName} earlier`}
            disabledReason={
              position.index === 0 ? "Already first." : moving ? "Saving the order…" : undefined
            }
            onClick={() => onMove(-1)}
          >
            <ArrowLeft16Regular aria-hidden />
          </Button>
          <Button
            variant="ghost"
            aria-label={`Move ${tier.displayName} later`}
            disabledReason={
              position.index === position.count - 1
                ? "Already last."
                : moving
                  ? "Saving the order…"
                  : undefined
            }
            onClick={() => onMove(1)}
          >
            <ArrowRight16Regular aria-hidden />
          </Button>
        </span>
      }
    >
      <div className="gp-stack">
        <p className="t-title-md" style={{ margin: 0 }}>
          {tier.monthlyPriceCents === null || tier.monthlyPriceCents === undefined
            ? "Custom"
            : `${formatMoney(tier.monthlyPriceCents)} / month`}
        </p>
        {tier.annualPriceCents !== null && tier.annualPriceCents !== undefined ? (
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {formatMoney(tier.annualPriceCents)} / year · {formatNumber(tier.customersOnTier ?? 0)} customers
          </p>
        ) : (
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {formatNumber(tier.customersOnTier ?? 0)} customers
          </p>
        )}
        {pending ? (
          <Banner tone="warn">
            New price {formatMoney(pending.newMonthlyPriceCents)} / month from{" "}
            {formatDate(pending.effectiveNotBefore)} (at renewal).
          </Banner>
        ) : null}
        <Field label="Display name" error={errs.displayName}>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Teams limit" help="-1 means unlimited." error={errs.teamsLimit}>
          <Input type="number" min={-1} value={limit} onChange={(e) => setLimit(e.target.value)} />
        </Field>
        <Toggle label="Most popular" checked={popular} onChange={setPopular} />
        <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
          Customers see this tier with a "Most popular" badge on their Subscription page. Only one tier
          carries it: choosing this one removes it from the other.
        </p>
        {editablePrice ? (
          <>
            <Field
              label="Monthly price (EUR)"
              help="Nobody pays this tier yet: the price can be set here. Once customers pay, it changes through an announced price change."
              error={errs.monthlyPriceCents}
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
              />
            </Field>
            <Field
              label="Annual price (EUR)"
              help="Leave empty to bill 10 months a year."
              error={errs.annualPriceCents}
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                value={annual}
                onChange={(e) => setAnnual(e.target.value)}
              />
            </Field>
          </>
        ) : tier.planKey === "enterprise" ? (
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            Enterprise has no list price: set a custom price per customer with an override.
          </p>
        ) : null}
        <div className="gp-actions">
          {!editablePrice && tier.planKey !== "enterprise" ? (
            <Button onClick={() => setScheduling(true)}>Change price</Button>
          ) : null}
          <Button
            variant="primary"
            disabledReason={
              !dirty ? "Nothing to save." : invalid ? "Check the name and the limit." : undefined
            }
            onClick={() => setConfirming(true)}
          >
            Save changes
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title={`Save the ${tier.displayName} tier?`}
        description={`Teams limit ${limit === "-1" ? "unlimited" : limit}${priceDirty ? `, ${monthly} EUR / month` : ""}. This applies to every customer on the tier without an override.`}
        confirmLabel="Save changes"
        busy={save.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() =>
          save.mutate(
            {
              params: { key: tier.planKey ?? "" },
              body: {
                displayName: name.trim(),
                teamsLimit: Number(limit),
                popular,
                ...(priceDirty
                  ? {
                      monthlyPriceCents: toCents(monthly),
                      annualPriceCents: annual === "" ? null : toCents(annual),
                    }
                  : {}),
              },
            },
            {
              onSuccess: () => {
                setErrs({});
                setConfirming(false);
                toast.success(`${name.trim()} saved`);
              },
              onError: (e) => {
                setErrs(fieldErrorsOf(e));
                setConfirming(false);
                fail(e);
              },
            },
          )
        }
      />
      <PriceChange tier={tier} open={scheduling} onClose={() => setScheduling(false)} />
    </Card>
  );
}

function PriceChange({ tier, open, onClose }: { tier: Tier; open: boolean; onClose: () => void }) {
  const fail = useHandleApiError();
  const toast = useToast();
  const schedule = useWrite<Tier, Schema<"PriceChange">>("POST", "/plans/:key/price-change", ["/plans"]);
  const [monthly, setMonthly] = useState(euros(tier.monthlyPriceCents));
  const [annual, setAnnual] = useState(euros(tier.annualPriceCents));
  const [date, setDate] = useState(new Date(Date.now() + 31 * DAY).toISOString().slice(0, 10));
  const early = new Date(`${date}T00:00:00Z`).getTime() < Date.now() + 30 * DAY;
  return (
    <ConfirmDialog
      open={open}
      title={`Change the ${tier.displayName} price`}
      description="Customers are told at least 30 days ahead. The new price applies at their next renewal; nobody is repriced mid-period."
      confirmLabel="Announce price change"
      busy={schedule.isPending}
      disabledReason={
        !(Number(monthly) >= 0) || monthly === ""
          ? "Enter the monthly price."
          : early
            ? "The date must be at least 30 days from now."
            : undefined
      }
      onClose={onClose}
      onConfirm={() =>
        schedule.mutate(
          {
            params: { key: tier.planKey ?? "" },
            body: {
              newMonthlyPriceCents: toCents(monthly),
              ...(annual ? { newAnnualPriceCents: toCents(annual) } : {}),
              effectiveNotBefore: `${date}T00:00:00.000Z`,
            },
          },
          {
            onSuccess: () => {
              toast.success("Price change announced");
              onClose();
            },
            onError: fail,
          },
        )
      }
    >
      <Field label="New monthly price (EUR)">
        <Input
          type="number"
          min={0}
          step="0.01"
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
        />
      </Field>
      <Field label="New annual price (EUR)">
        <Input type="number" min={0} step="0.01" value={annual} onChange={(e) => setAnnual(e.target.value)} />
      </Field>
      <Field label="Effective not before" error={early ? "At least 30 days from now." : undefined}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}

function NewOverride({ open, onClose }: { open: boolean; onClose: () => void }) {
  const fail = useHandleApiError();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [orgId, setOrgId] = useState("");
  const [type, setType] = useState<Schema<"PriceOverrideCreate">["type"]>("custom_price");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const found = useGet<{ items: Customer[] }>(
    "/customers",
    { q: search, limit: 20 },
    { enabled: open && search.trim().length > 1 },
  );
  const create = useWrite<Override, Schema<"PriceOverrideCreate">>("POST", "/price-overrides", [
    "/price-overrides",
    "/customers",
  ]);
  const options = [
    { value: "", label: "Choose a customer" },
    ...(found.data?.items ?? [])
      .filter((c) => c.organizationId)
      .map((c) => ({ value: c.organizationId ?? "", label: `${c.name} (${c.primaryDomain})` })),
  ];
  const bad = !orgId || !reason.trim() || value === "" || Number(value) < (type === "teams_limit" ? -1 : 0);
  return (
    <ConfirmDialog
      open={open}
      title="New override"
      description="Changes what one customer is billed or entitled to, without touching the tier."
      confirmLabel="Create override"
      busy={create.isPending}
      disabledReason={bad ? "Choose a customer, a value and a reason." : undefined}
      onClose={onClose}
      onConfirm={() =>
        create.mutate(
          {
            body: {
              organizationId: orgId,
              type,
              reason: reason.trim(),
              ...(type === "custom_price" ? { valueCents: toCents(value) } : { valueInt: Number(value) }),
            },
          },
          {
            onSuccess: () => {
              toast.success("Override created");
              setOrgId("");
              setValue("");
              setReason("");
              onClose();
            },
            onError: fail,
          },
        )
      }
    >
      <Field label="Find a customer">
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </Field>
      <Field label="Customer">
        <SelectX value={orgId} options={options} onChange={(e) => setOrgId(e.target.value)} />
      </Field>
      <Field label="Type">
        <SelectX
          value={type}
          options={[
            { value: "custom_price", label: "Custom price (EUR / month)" },
            { value: "teams_limit", label: "Teams limit (-1 unlimited)" },
          ]}
          onChange={(e) => setType(e.target.value as typeof type)}
        />
      </Field>
      <Field label="Value">
        <Input
          type="number"
          step={type === "custom_price" ? "0.01" : "1"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Field>
      <Field label="Reason">
        <Input value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}
